/**
 * Promo line under "Get started" on the Get Started forms: the homepage and
 * /lp/google-v2 forms (WEBDEV-4371) and the site-wide on-page self-schedule form
 * in Layout (WEBDEV-4388). Both the legacy ConsultationForm and the on-page
 * self-schedule form show it in place of their default subtitle when their
 * placement opts in, and go back to that subtitle once no campaign is live.
 *
 * Driven by a single env var holding a JSON array of campaigns, so a new promo is
 * a Netlify change plus a deploy rather than a code change:
 *
 *   GATSBY_FORM_PROMOS=[{"text":"…","start":"2026-10-01T00:00:00-05:00","end":"2026-10-21T23:59:59-05:00","placements":["home","google-v2"]}]
 *
 *   text        the copy, rendered as plain text (required)
 *   end         ISO 8601 with an offset (required)
 *   start       ISO 8601 with an offset (optional; live from the first deploy if omitted)
 *   placements  which forms show it (optional; every placement if omitted)
 *
 * Several campaigns can be queued at once, and overlapping ones can target
 * different placements. When more than one is live for a placement, the first in
 * the array wins. Include the offset on dates: without one they're read in each
 * visitor's own timezone. Bad JSON or a malformed campaign fails the build rather
 * than quietly rendering nothing.
 *
 * QA can preview an upcoming campaign by setting its start in the past in the QA
 * context, and test expiry by setting its end a few minutes out.
 *
 * GATSBY_-prefixed vars are inlined at build time, so "no code changes" still
 * means a redeploy for every change to this var. The start and end are checked
 * against the build time for the static HTML and again on the client (see
 * useFormPromoText), so a campaign also turns on and off on its own between
 * deploys, but it can show or hide briefly after hydration until the next build.
 * A scheduled Netlify build hook at each start date gives the cleanest result. If
 * marketing ever needs to schedule these without a deploy, Builder.io's
 * startDate / endDate may be a better home for it.
 */

export const DEFAULT_FORM_SUBTITLE = 'Book today and lock in your offer.';

export const FORM_PROMO_PLACEMENTS = ['home', 'google-v2', 'site-wide'] as const;

export type FormPromoPlacement = (typeof FORM_PROMO_PLACEMENTS)[number];

export interface FormPromo {
    text: string;
    startMs: number | null;
    endMs: number;
    placements: readonly FormPromoPlacement[];
}

function fail(message: string): never {
    throw new Error(`GATSBY_FORM_PROMOS: ${message}`);
}

function parseDate(value: unknown, field: string, index: number): number {
    const ms = typeof value === 'string' ? Date.parse(value) : NaN;
    if (Number.isNaN(ms)) fail(`campaign ${index} has an invalid "${field}" date: ${JSON.stringify(value)}`);
    return ms;
}

function parseFormPromos(raw: string | undefined): FormPromo[] {
    const json = raw?.trim();
    if (!json) return [];

    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch (error) {
        fail(`not valid JSON (${(error as Error).message})`);
    }
    if (!Array.isArray(parsed)) fail('must be a JSON array of campaigns');

    return parsed.map((entry, index): FormPromo => {
        if (typeof entry !== 'object' || entry === null) fail(`campaign ${index} must be an object`);
        const { text, start, end, placements } = entry as Record<string, unknown>;

        if (typeof text !== 'string' || text.trim() === '') fail(`campaign ${index} needs a non-empty "text"`);

        const startMs = start === undefined ? null : parseDate(start, 'start', index);
        const endMs = parseDate(end, 'end', index);
        if (startMs !== null && startMs >= endMs) fail(`campaign ${index} has a "start" on or after its "end"`);

        if (placements !== undefined) {
            if (!Array.isArray(placements) || placements.length === 0) {
                fail(`campaign ${index} "placements" must be a non-empty array if set`);
            }
            const unknown = placements.filter((p) => !FORM_PROMO_PLACEMENTS.includes(p));
            if (unknown.length) {
                fail(
                    `campaign ${index} has unknown placements ${JSON.stringify(unknown)}; expected any of ${JSON.stringify(FORM_PROMO_PLACEMENTS)}`
                );
            }
        }

        return {
            text: text.trim(),
            startMs,
            endMs,
            placements: (placements as FormPromoPlacement[] | undefined) ?? FORM_PROMO_PLACEMENTS,
        };
    });
}

export const FORM_PROMOS: readonly FormPromo[] = parseFormPromos(process.env.GATSBY_FORM_PROMOS);

/**
 * When this build ran, set in gatsby-config.js. The static HTML (and the client's
 * first render, so hydration matches) picks the live campaign as of this moment.
 */
export const BUILD_TIME_MS = Date.parse(process.env.GATSBY_BUILD_TIME ?? '') || 0;

export function isFormPromoLive(promo: FormPromo, nowMs: number): boolean {
    return (promo.startMs === null || nowMs >= promo.startMs) && nowMs <= promo.endMs;
}
